import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Archive, Upload, Hammer, CalendarCheck, Smartphone, Trash2, Brush, ArrowUpRight, ListOrdered, Eye } from "lucide-react";
import { api, API, errMsg } from "@/lib/api";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

export const STEP2 = "Coming in step 2, when the relocation guide builder moves into this studio";
const btn = "inline-flex items-center gap-2 border border-[#E2D9CC] bg-[#FAF7F2] px-4 py-2.5 text-sm text-[#1C181D] transition-colors hover:border-[#3B1B48] disabled:opacity-45 disabled:hover:border-[#E2D9CC] disabled:cursor-not-allowed";
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

export const Step2Button = ({ icon: Icon, label, testId, className = btn }) => (
  <button data-testid={testId} disabled title={STEP2} className={className}><Icon className="w-4 h-4" strokeWidth={1.5} />{label}</button>
);

const CONFIRM = {
  update: "Re-research every source and rewrite every chapter as a new edition? This takes 20-40 minutes and uses AI credit.",
  redraw: "Redraw the cover and all 25 illustrations? This uses AI image credit, then re-typesets the files.",
  index: "Build a back-of-book index with page numbers for this layout? Any index copied from the original book is removed first. Uses a little AI credit, then re-typesets the files.",
};

export const BuildButton = ({ g, mode, icon: Icon, label, testId, className = btn, onStarted }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (CONFIRM[mode] && !window.confirm(CONFIRM[mode])) return;
    setBusy(true);
    try { await api.post(`/relocation/guides/${g.id}/build/${mode}`); toast.success(`${label} started`); onStarted(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return <button data-testid={testId} onClick={go} disabled={busy || g.running || !g.volumes.length} className={className}><Icon className="w-4 h-4" strokeWidth={1.5} />{label}</button>;
};

export const RelocHeader = ({ g }) => (
  <div className="flex flex-col sm:flex-row gap-7 items-start" data-testid="reloc-header">
    <div className="shrink-0 w-36">
      {g.has_cover && <img src={`${API}/relocation/guides/${g.id}/files/cover?inline=true`} alt={g.title} className="w-36 aspect-[3/4] object-cover shadow-md" data-testid="reloc-cover" />}
      <div className="grid grid-cols-2 gap-1 mt-1">
        <Step2Button icon={Brush} label="" testId="reloc-cover-redraw" className="border border-[#E2D9CC] py-1.5 grid place-items-center text-[#3B1B48] disabled:opacity-45" />
        <Step2Button icon={Upload} label="" testId="reloc-cover-upload" className="border border-[#E2D9CC] py-1.5 grid place-items-center text-[#3B1B48] disabled:opacity-45" />
      </div>
    </div>
    <div className="min-w-0">
      <p className="eyebrow flex flex-wrap items-center gap-3">{g.origin} → {g.destination}{g.edition ? ` · Edition ${g.edition}` : ""}
        <span data-testid="reloc-publish-state" className={`normal-case tracking-normal font-sans text-xs px-2.5 py-0.5 rounded-full ${g.published ? "bg-[#F2E7C4] text-[#7A5A10]" : "bg-[#EEE] text-[#8C827A]"}`}>{g.running ? "building" : g.status === "failed" ? "stopped" : g.published ? "published" : "draft"}</span>
      </p>
      <h1 data-testid="reloc-title" className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#1C181D] leading-[1.05] mt-3 max-w-3xl">{g.title}</h1>
      <p className="font-display italic text-xl text-[#8C827A] mt-3">{g.subtitle}</p>
    </div>
  </div>
);

const DeleteGuide = ({ g, onDeleted }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild><button data-testid="reloc-delete" className={btn}><Trash2 className="w-4 h-4" strokeWidth={1.5} />Delete</button></AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle className="font-display">Delete this guide from the studio?</AlertDialogTitle>
        <AlertDialogDescription>Removes this copy, its files and chapters, and takes it off the store feed. Running an import again would copy it back from Relocation Guides.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="reloc-delete-cancel">Cancel</AlertDialogCancel>
        <AlertDialogAction data-testid="reloc-delete-confirm" onClick={async () => { try { await api.delete(`/relocation/guides/${g.id}`); toast.success("Guide deleted"); onDeleted(); } catch (e) { toast.error(errMsg(e)); } }}>Delete</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const RelocActions = ({ g, setG, onDeleted, onSynced }) => {
  const [busy, setBusy] = useState(null);
  const toggle = async () => {
    setBusy("pub");
    try {
      const { data } = await api.post(`/relocation/guides/${g.id}/${g.published ? "unpublish" : "publish"}`);
      setG({ ...g, published: data.published });
      toast.success(data.published ? "Listed in the store feed" : "Removed from the store feed");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  return (
    <div className="mt-8 w-fit max-w-full" data-testid="reloc-actions">
      <div className="flex flex-wrap gap-2">
        <button data-testid="reloc-publish-toggle" onClick={toggle} disabled={!!busy || !["vol1", "vol2", "vol3", "complete", "toolkit", "bundle"].some((k) => g.files[k])} className={btn}>
          {g.published ? <><Archive className="w-4 h-4" strokeWidth={1.5} />Unpublish from store</> : <><Upload className="w-4 h-4" strokeWidth={1.5} />Publish to store</>}
        </button>
        <BuildButton g={g} mode="rebuild" icon={Hammer} label="Rebuild files" testId="reloc-rebuild" onStarted={onSynced} />
        <BuildButton g={g} mode="index" icon={ListOrdered} label={g.has_index ? "Rebuild index" : "Add index"} testId="reloc-index" onStarted={onSynced} />
        <Step2Button icon={CalendarCheck} label="Customer plan page" testId="reloc-plan-page" />
        {g.files.toolkit && <a data-testid="reloc-toolkit-app" href={`${API}/relocation/guides/${g.id}/files/toolkit?inline=true`} target="_blank" rel="noreferrer" className={btn}><Smartphone className="w-4 h-4" strokeWidth={1.5} />Toolkit app</a>}
        {g.files.toolkit && g.published && <a data-testid="reloc-app-preview" href={`${API}/relocation/store/products/${g.id}/app-preview.html`} target="_blank" rel="noreferrer" className={btn} title="The free preview shown on the shop page: chapter 1, other chapters locked"><Eye className="w-4 h-4" strokeWidth={1.5} />Shop app preview</a>}
        <DeleteGuide g={g} onDeleted={onDeleted} />
      </div>
      <div className="mt-4 text-xs text-[#8C827A] space-y-1.5 sm:text-right">
        <p className="flex sm:justify-end items-center gap-2">
          <span className={`w-1.5 h-1.5 rounded-full ${g.published ? "bg-[#2D6A4F]" : "bg-[#8C827A]"}`} />
          <span data-testid="reloc-sync-state">{g.published ? "Listed" : "Not listed"}{g.imported_at ? ` · copied ${fmt(g.imported_at)}` : ""}</span>
        </p>
        <p>{g.published ? "Listed in this studio's relocation product feed for theexpatstudio.com." : "Not in the product feed. Publish to list it on theexpatstudio.com."}</p>
        <p><Link to="/relocation" className="hover:text-[#3B1B48]">Set up the store connection →</Link></p>
        {g.published && <p><a data-testid="reloc-whats-new-link" href={`${API}/relocation/share/whats-new/${g.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-[#3B1B48]">Public "what's new" page <ArrowUpRight className="w-3 h-3" /></a></p>}
      </div>
    </div>
  );
};
